<template>
  <div
    class="roulette-bet-carousel-wrapper w-full bg-white rounded-2xl p-3.5 text-slate-800 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-sky-100 outline-none"
    tabindex="0"
    @keydown.left.prevent="prevSlide"
    @keydown.right.prevent="nextSlide"
  >
    <!-- Carousel Header / Navigation Controls -->
    <div
      class="flex items-center justify-between flex-wrap gap-2 mb-2.5 pb-2 border-b border-sky-100/80"
    >
      <div class="flex items-center gap-2.5">
        <div
          class="w-7 h-7 rounded-xl bg-[#eef6fa] text-[#2b79a8] flex items-center justify-center text-xs shadow-xs border border-sky-200/50"
        >
          <i class="fa-solid fa-table-cells"></i>
        </div>
        <div>
          <h4
            class="text-xs font-bold text-slate-800 m-0 tracking-tight flex items-center gap-1.5"
          >
            Bet Board Visualizer
            <span
              class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              :class="
                isAllMode
                  ? 'bg-emerald-100 text-emerald-800 font-bold'
                  : 'text-slate-500 bg-slate-100'
              "
            >
              {{
                isAllMode
                  ? `All ${slides.length} Bets`
                  : slides.length > 1
                  ? `Bet ${currentSlideIndex + 1} of ${slides.length}`
                  : "1 Bet"
              }}
            </span>
          </h4>
        </div>
      </div>

      <!-- View Mode Tabs (Both | Table | Racetrack) -->
      <div
        class="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200 ms-auto"
      >
        <button
          @click="activeViewTab = 'both'"
          type="button"
          :class="[
            'px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer',
            activeViewTab === 'both'
              ? 'bg-white text-[#2b79a8] shadow-xs'
              : 'text-slate-500 hover:text-slate-800',
          ]"
          title="Show Both Table and Racetrack"
        >
          <i class="fa-solid fa-table-columns text-[10px]"></i>
          <span>Both</span>
        </button>

        <button
          @click="activeViewTab = 'table'"
          type="button"
          :class="[
            'px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer',
            activeViewTab === 'table'
              ? 'bg-white text-[#2b79a8] shadow-xs'
              : 'text-slate-500 hover:text-slate-800',
          ]"
          title="Show Table only"
        >
          <i class="fa-solid fa-table-cells text-[10px]"></i>
          <span>Table</span>
        </button>

        <button
          @click="activeViewTab = 'racetrack'"
          type="button"
          :class="[
            'px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer',
            activeViewTab === 'racetrack'
              ? 'bg-white text-[#2b79a8] shadow-xs'
              : 'text-slate-500 hover:text-slate-800',
          ]"
          title="Show Racetrack only"
        >
          <i class="fa-solid fa-bullseye text-[10px]"></i>
          <span>Racetrack</span>
        </button>
      </div>

      <!-- Apple-style Carousel Navigation Buttons & Counter -->
      <div class="flex items-center gap-1.5" v-if="slides.length > 1">
        <button
          @click="prevSlide"
          class="w-7 h-7 rounded-full bg-slate-100 hover:bg-[#eef6fa] text-slate-600 hover:text-[#2b79a8] flex items-center justify-center text-xs transition-all cursor-pointer border border-slate-200 hover:border-sky-200 shadow-2xs active:scale-95"
          title="Previous Bet (Left Arrow)"
        >
          <i class="fa-solid fa-chevron-left text-[10px]"></i>
        </button>

        <span
          class="text-[11px] font-bold px-2 py-0.5 rounded-full border select-none"
          :class="
            isAllMode
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 font-bold'
              : 'bg-slate-100 text-slate-700 border-slate-200'
          "
        >
          {{
            isAllMode ? "ALL" : `${currentSlideIndex + 1} / ${slides.length}`
          }}
        </span>

        <button
          @click="nextSlide"
          class="w-7 h-7 rounded-full bg-slate-100 hover:bg-[#eef6fa] text-slate-600 hover:text-[#2b79a8] flex items-center justify-center text-xs transition-all cursor-pointer border border-slate-200 hover:border-sky-200 shadow-2xs active:scale-95"
          title="Next Bet (Right Arrow)"
        >
          <i class="fa-solid fa-chevron-right text-[10px]"></i>
        </button>
      </div>
    </div>

    <!-- Apple-style Bet Quick Jump Pills -->
    <div
      v-if="slides.length > 1"
      ref="pillsContainer"
      class="flex items-center gap-1.5 overflow-x-auto pb-2 mb-2.5 scrollbar-thin select-none scroll-smooth"
    >
      <!-- ALL Bets Pill -->
      <button
        ref="pillAllBtn"
        @click="currentSlideIndex = 'all'"
        :class="[
          'px-3.5 py-1 rounded-full text-[11px] whitespace-nowrap transition-all cursor-pointer border shadow-2xs flex items-center gap-1.5 flex-shrink-0',
          isAllMode
            ? 'bg-gradient-to-r from-emerald-600 to-teal-700 text-white border-emerald-600 shadow-sm font-bold scale-[1.02]'
            : 'bg-slate-50 text-slate-700 hover:bg-[#eef6fa] hover:text-[#2b79a8] hover:border-sky-200 border-slate-200 font-semibold',
        ]"
      >
        <i class="fa-solid fa-layer-group text-[10px]"></i>
        <span>ALL Bets ({{ slides.length }})</span>
      </button>

      <!-- Individual Bet Pills -->
      <button
        v-for="(slide, idx) in slides"
        :key="idx"
        :ref="(el) => { if (el) pillRefs[idx] = el; }"
        @click="currentSlideIndex = idx"
        :class="[
          'px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer border shadow-2xs flex-shrink-0',
          !isAllMode && currentSlideIndex === idx
            ? 'bg-gradient-to-r from-[#2b79a8] to-[#1e618b] text-white border-[#2b79a8] shadow-sm font-bold scale-[1.02]'
            : 'bg-slate-50 text-slate-600 hover:bg-[#eef6fa] hover:text-[#2b79a8] hover:border-sky-200 border-slate-200',
        ]"
      >
        <span>
          #{{ slide.bet.type_id }} •
          {{ slide.info.label || `Type ${slide.bet.type_id}` }}
        </span>
      </button>
    </div>

    <!-- Apple-style Active Bet Summary Card (Soft Azzurrino Glow) -->
    <div
      v-if="slides.length > 0"
      class="bg-gradient-to-r from-[#f0f7fb] via-[#e8f3f9] to-[#f0f7fb] rounded-xl p-2.5 mb-2.5 border border-sky-200/80 shadow-xs flex items-center justify-between flex-wrap gap-2 text-xs"
    >
      <div class="flex items-center gap-2">
        <span
          class="px-2.5 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider shadow-2xs"
          :class="isAllMode ? 'bg-emerald-600' : 'bg-[#2b79a8]'"
        >
          {{ isAllMode ? "All Bets" : currentSlide.info.category }}
        </span>
        <span class="font-bold text-slate-800 text-xs">
          {{
            isAllMode
              ? `Total of ${slides.length} Bets Placed`
              : currentSlide.info.label
          }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <!-- Bet Amount -->
        <div
          class="flex items-center gap-1 bg-white px-2.5 py-1 rounded-full border border-sky-100 shadow-2xs"
        >
          <span class="text-[10px] text-slate-400 uppercase font-bold">
            {{ isAllMode ? "Total Bet:" : "Amount:" }}
          </span>
          <span class="font-bold text-slate-900 text-xs">
            {{
              formatAmount(isAllMode ? totalAllBetsAmount : currentSlide.amount)
            }}
            {{ currency }}
          </span>
        </div>

        <!-- Payout (Single bet only) -->
        <div
          class="flex items-center gap-1 bg-white px-2.5 py-1 rounded-full border border-sky-100 shadow-2xs"
          v-if="!isAllMode && currentSlide && currentSlide.info.payout > 0"
        >
          <span class="text-[10px] text-slate-400 uppercase font-bold"
            >Payout:</span
          >
          <span class="font-bold text-[#2b79a8] text-xs">
            {{ currentSlide.info.payout }}:1
          </span>
        </div>

        <!-- Covered Count -->
        <div
          class="flex items-center gap-1 bg-white px-2.5 py-1 rounded-full border border-sky-100 shadow-2xs"
        >
          <span class="text-[10px] text-slate-400 uppercase font-bold"
            >Covered:</span
          >
          <span class="font-bold text-slate-800 text-[10px]">
            {{ currentCoveredNumbers.size }} / 37
          </span>
        </div>
      </div>
    </div>

    <!-- Visualizer Components Area (Side-by-Side on XL screens, switchable on smaller) -->
    <div
      :class="[
        'w-full transition-all',
        activeViewTab === 'both'
          ? 'grid grid-cols-1 xl:grid-cols-2 gap-3.5 items-center'
          : 'flex flex-col gap-3',
      ]"
    >
      <!-- 1. Interactive Roulette Board Component (Cloth Layout) -->
      <div
        v-if="activeViewTab === 'table' || activeViewTab === 'both'"
        ref="tableContainer"
        class="roulette-board-container overflow-x-auto pb-1 w-full scroll-smooth"
      >
        <div
          v-if="activeViewTab === 'both'"
          class="flex items-center gap-1.5 px-1 mb-1.5"
        >
          <span
            class="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded"
          >
            Table Layout
          </span>
        </div>
        <div
          class="roulette-board-cloth select-none rounded-xl p-1 flex flex-col"
        >
          <!-- TOP SECTION: ZERO + 3x12 NUMBERS + 2:1 COLUMNS -->
          <div class="roulette-top-grid">
            <!-- ZERO (Col spanning 3 rows on left) -->
            <div
              :class="[
                'cell cell-zero rounded-tl-lg rounded-bl-lg',
                isNumberCovered(0) ? 'cell-highlight-number' : '',
              ]"
            >
              <span class="num-text">0</span>
              <span v-if="chipOnZero" class="board-chip">
                {{ chipOnZero }}
              </span>
              <div
                v-if="isWinningNumber(0)"
                class="winning-marker-ring"
                title="Winning Number: 0"
              ></div>
            </div>

            <!-- NUMBERS GRID (3 Rows x 12 Columns) -->
            <div class="numbers-grid relative">
              <!-- Row 1: 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36 -->
              <div class="grid-row">
                <div
                  v-for="num in row1Numbers"
                  :key="num"
                  :class="[
                    'cell cell-number',
                    isRed(num) ? 'cell-red' : 'cell-black',
                    isNumberCovered(num) ? 'cell-highlight-number' : '',
                  ]"
                >
                  <span class="num-text">{{ num }}</span>
                  <div
                    v-if="isWinningNumber(num)"
                    class="winning-marker-ring"
                    :title="`Winning Number: ${num}`"
                  ></div>
                </div>
              </div>

              <!-- Row 2: 2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35 -->
              <div class="grid-row">
                <div
                  v-for="num in row2Numbers"
                  :key="num"
                  :class="[
                    'cell cell-number',
                    isRed(num) ? 'cell-red' : 'cell-black',
                    isNumberCovered(num) ? 'cell-highlight-number' : '',
                  ]"
                >
                  <span class="num-text">{{ num }}</span>
                  <div
                    v-if="isWinningNumber(num)"
                    class="winning-marker-ring"
                    :title="`Winning Number: ${num}`"
                  ></div>
                </div>
              </div>

              <!-- Row 3: 1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34 -->
              <div class="grid-row">
                <div
                  v-for="num in row3Numbers"
                  :key="num"
                  :class="[
                    'cell cell-number',
                    isRed(num) ? 'cell-red' : 'cell-black',
                    isNumberCovered(num) ? 'cell-highlight-number' : '',
                  ]"
                >
                  <span class="num-text">{{ num }}</span>
                  <div
                    v-if="isWinningNumber(num)"
                    class="winning-marker-ring"
                    :title="`Winning Number: ${num}`"
                  ></div>
                </div>
              </div>

              <!-- Inside Board Sub-Chips Layer Overlay -->
              <div
                class="board-chips-overlay pointer-events-none absolute inset-0 z-20 overflow-visible"
              >
                <div
                  v-for="(chip, idx) in activeInsideChips"
                  :key="idx"
                  class="board-chip"
                  :style="{ left: chip.x + '%', top: chip.y + '%' }"
                >
                  {{ chip.amount }}
                </div>
              </div>
            </div>

            <!-- 2:1 COLUMN BETS (Right side, 3 rows) -->
            <div class="columns-bet-group">
              <!-- Col 3 (Row 1) -> type_id 151 -->
              <div
                :class="[
                  'cell cell-column rounded-tr-lg',
                  isTypeIdActive(151) ? 'cell-highlight-outside' : '',
                ]"
              >
                <span class="label-text">2 to 1</span>
                <span v-if="getChipOnTypeId(151)" class="board-chip">
                  {{ getChipOnTypeId(151) }}
                </span>
              </div>
              <!-- Col 2 (Row 2) -> type_id 150 -->
              <div
                :class="[
                  'cell cell-column',
                  isTypeIdActive(150) ? 'cell-highlight-outside' : '',
                ]"
              >
                <span class="label-text">2 to 1</span>
                <span v-if="getChipOnTypeId(150)" class="board-chip">
                  {{ getChipOnTypeId(150) }}
                </span>
              </div>
              <!-- Col 1 (Row 3) -> type_id 149 -->
              <div
                :class="[
                  'cell cell-column rounded-br-lg',
                  isTypeIdActive(149) ? 'cell-highlight-outside' : '',
                ]"
              >
                <span class="label-text">2 to 1</span>
                <span v-if="getChipOnTypeId(149)" class="board-chip">
                  {{ getChipOnTypeId(149) }}
                </span>
              </div>
            </div>
          </div>

          <!-- DOZENS ROW (Attached directly below numbers) -->
          <div class="roulette-dozens-row select-none">
            <div class="dozen-spacer"></div>
            <!-- 1st 12 -> type_id 146 -->
            <div
              :class="[
                'cell cell-dozen',
                isTypeIdActive(146) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">1st 12</span>
              <span v-if="getChipOnTypeId(146)" class="board-chip">
                {{ getChipOnTypeId(146) }}
              </span>
            </div>
            <!-- 2nd 12 -> type_id 147 -->
            <div
              :class="[
                'cell cell-dozen',
                isTypeIdActive(147) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">2nd 12</span>
              <span v-if="getChipOnTypeId(147)" class="board-chip">
                {{ getChipOnTypeId(147) }}
              </span>
            </div>
            <!-- 3rd 12 -> type_id 148 -->
            <div
              :class="[
                'cell cell-dozen',
                isTypeIdActive(148) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">3rd 12</span>
              <span v-if="getChipOnTypeId(148)" class="board-chip">
                {{ getChipOnTypeId(148) }}
              </span>
            </div>
            <div class="dozen-spacer-right"></div>
          </div>

          <!-- OUTSIDE CHANCES ROW (Attached directly below dozens) -->
          <div class="roulette-outside-row select-none">
            <div class="dozen-spacer"></div>
            <!-- 1-18 -> type_id 156 (Outer-left bottom rounded) -->
            <div
              :class="[
                'cell cell-outside rounded-bl-lg',
                isTypeIdActive(156) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">1-18</span>
              <span v-if="getChipOnTypeId(156)" class="board-chip">
                {{ getChipOnTypeId(156) }}
              </span>
            </div>
            <!-- EVEN -> type_id 154 -->
            <div
              :class="[
                'cell cell-outside',
                isTypeIdActive(154) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">EVEN</span>
              <span v-if="getChipOnTypeId(154)" class="board-chip">
                {{ getChipOnTypeId(154) }}
              </span>
            </div>
            <!-- RED -> type_id 152 -->
            <div
              :class="[
                'cell cell-outside cell-red-outside',
                isTypeIdActive(152) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span v-if="getChipOnTypeId(152)" class="board-chip">
                {{ getChipOnTypeId(152) }}
              </span>
            </div>
            <!-- BLACK -> type_id 153 -->
            <div
              :class="[
                'cell cell-outside cell-black-outside',
                isTypeIdActive(153) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span v-if="getChipOnTypeId(153)" class="board-chip">
                {{ getChipOnTypeId(153) }}
              </span>
            </div>
            <!-- ODD -> type_id 155 -->
            <div
              :class="[
                'cell cell-outside',
                isTypeIdActive(155) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">ODD</span>
              <span v-if="getChipOnTypeId(155)" class="board-chip">
                {{ getChipOnTypeId(155) }}
              </span>
            </div>
            <!-- 19-36 -> type_id 157 (Outer-right bottom rounded) -->
            <div
              :class="[
                'cell cell-outside rounded-br-lg',
                isTypeIdActive(157) ? 'cell-highlight-outside' : '',
              ]"
            >
              <span class="label-text">19-36</span>
              <span v-if="getChipOnTypeId(157)" class="board-chip">
                {{ getChipOnTypeId(157) }}
              </span>
            </div>
            <div class="dozen-spacer-right"></div>
          </div>
        </div>
      </div>

      <!-- 2. Interactive French Racetrack Track Layout -->
      <div
        v-if="activeViewTab === 'racetrack' || activeViewTab === 'both'"
        class="roulette-racetrack-section pb-1 w-full"
      >
        <div
          v-if="activeViewTab === 'both'"
          class="flex items-center gap-1.5 px-1 mb-1.5"
        >
          <span
            class="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded"
          >
            Wheel Racetrack
          </span>
        </div>
        <RouletteRacetrack
          :bets="bets"
          :current-slide-index="currentSlideIndex"
          :currency="currency"
          :winning-number="winningNumber"
        />
      </div>
    </div>

    <!-- Active Bet Covered Numbers List Footer (3D Roulette Chips) -->
    <div
      v-if="currentCoveredNumbers.size > 0"
      class="mt-2.5 pt-2 border-t border-sky-100 flex items-center flex-wrap gap-1.5 text-[11px]"
    >
      <span
        class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1"
      >
        Covered ({{ currentCoveredNumbers.size }}):
      </span>
      <span
        v-for="num in sortedCoveredNumbers"
        :key="num"
        :class="['roulette-chip chip-table', getChipColorClass(num)]"
      >
        <span class="chip-number">{{ num }}</span>
      </span>
    </div>

    <!-- Winning Breakdown (Shown only if win or win_converted > 0) -->
    <div
      v-if="hasWinnings"
      class="winning-breakdown-container bg-gradient-to-r from-emerald-50/70 via-[#f4fbf7] to-emerald-50/70 rounded-xl p-3 mt-3 border border-emerald-200/80 shadow-xs text-slate-800 select-none"
    >
      <!-- Header row -->
      <div
        class="flex items-center justify-between flex-wrap gap-2 mb-2 pb-2 border-b border-emerald-100"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs shadow-2xs border border-emerald-200/70"
          >
            <i class="fa-solid fa-trophy text-[11px]"></i>
          </div>
          <h5
            class="text-xs font-bold text-slate-800 m-0 tracking-tight flex items-center gap-1.5"
          >
            Winning Breakdown
          </h5>
        </div>
      </div>

      <!-- Winning Cards Grid -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5"
      >
        <div
          v-for="(winCard, idx) in finalWinningCards"
          :key="idx"
          class="bg-white border border-emerald-200/80 rounded-xl p-2.5 flex flex-col justify-between shadow-2xs hover:border-emerald-400 hover:shadow-xs transition-all min-w-0"
        >
          <div
            class="text-xs font-bold text-slate-800 truncate mb-1"
            :title="winCard.label"
          >
            {{ winCard.label }}
          </div>
          <div
            class="text-[11px] text-slate-500 font-medium flex items-center gap-1.5 flex-wrap"
          >
            <span
              >Bet:
              <strong class="text-slate-700 font-semibold">{{
                formatCurrency(winCard.betAmount)
              }}</strong></span
            >
            <span
              v-if="winCard.payoutRatio && winCard.payoutRatio !== '-'"
              class="text-slate-300"
              >•</span
            >
            <span
              v-if="winCard.payoutRatio && winCard.payoutRatio !== '-'"
              class="text-slate-500"
            >
              Pays:
              <strong class="text-[#2b79a8] font-bold">{{
                winCard.payoutRatio
              }}</strong>
            </span>
          </div>
          <div
            class="mt-2 pt-1.5 border-t border-emerald-50 flex items-center justify-between text-xs"
          >
            <span class="text-[10px] font-bold uppercase text-slate-400"
              >Win</span
            >
            <span class="font-extrabold text-emerald-600 text-xs">
              {{ formatCurrency(winCard.winAmount) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import rouletteBetTypes from "../config/rouletteBetTypes.json";
import RouletteRacetrack from "./RouletteRacetrack.vue";

const activeViewTab = ref("both");

const props = defineProps({
  bets: {
    type: Array,
    default: () => [],
  },
  currency: {
    type: String,
    default: "EUR",
  },
  winningNumber: {
    type: [Number, String],
    default: null,
  },
  win: {
    type: [Number, String],
    default: 0,
  },
  winConverted: {
    type: [Number, String],
    default: 0,
  },
});

function formatCurrency(val) {
  if (val === undefined || val === null || val === "") return "€ 0.00";
  const num = parseFloat(val) || 0;
  const curr = (props.currency || "EUR").toUpperCase();
  const symbolMap = {
    EUR: "€",
    USD: "$",
    GBP: "£",
    TRY: "₺",
    BRL: "R$",
  };
  const sym = symbolMap[curr] || props.currency || "€";
  return `${sym} ${num.toFixed(2)}`;
}

const hasWinnings = computed(() => {
  const winVal = parseFloat(props.win) || 0;
  const winConvVal = parseFloat(props.winConverted) || 0;
  return winVal > 0 || winConvVal > 0;
});

const winningBetsBreakdown = computed(() => {
  if (
    props.winningNumber === null ||
    props.winningNumber === undefined ||
    props.winningNumber === "" ||
    !props.bets ||
    props.bets.length === 0
  ) {
    return [];
  }

  const hitNumber = Number(props.winningNumber);
  if (isNaN(hitNumber)) return [];

  const wins = [];

  for (const bet of props.bets) {
    const typeId = Number(bet?.type_id);
    const amt = parseFloat(bet?.amount) || 0;
    if (amt <= 0) continue;

    const typeIdStr = String(typeId);
    const info = rouletteBetTypes[typeIdStr] || {
      type_id: typeId,
      label: `Type #${typeId}`,
      category: "Custom",
      payout: 0,
      numbers: [],
    };

    // 1. Straight Up (1 to 37 -> 0 to 36)
    if (typeId >= 1 && typeId <= 37) {
      const num = typeId - 1;
      if (num === hitNumber) {
        wins.push({
          label: `Straight ${num}`,
          betAmount: amt,
          payoutRatio: "35:1",
          winAmount: amt * 36,
        });
      }
      continue;
    }

    // 2. French Series: Jeu Zéro (158)
    if (typeId === 158) {
      const unit = amt / 4;
      if (hitNumber === 26) {
        wins.push({
          label: "Jeu Zéro (Straight 26)",
          betAmount: unit,
          payoutRatio: "35:1",
          winAmount: unit * 36,
        });
      }
      if (hitNumber === 0 || hitNumber === 3) {
        wins.push({
          label: "Jeu Zéro (Split 0/3)",
          betAmount: unit,
          payoutRatio: "17:1",
          winAmount: unit * 18,
        });
      }
      if (hitNumber === 12 || hitNumber === 15) {
        wins.push({
          label: "Jeu Zéro (Split 12/15)",
          betAmount: unit,
          payoutRatio: "17:1",
          winAmount: unit * 18,
        });
      }
      if (hitNumber === 32 || hitNumber === 35) {
        wins.push({
          label: "Jeu Zéro (Split 32/35)",
          betAmount: unit,
          payoutRatio: "17:1",
          winAmount: unit * 18,
        });
      }
      continue;
    }

    // 3. French Series: Voisins du Zéro (159)
    if (typeId === 159) {
      const unit = amt / 9;
      if ([0, 2, 3].includes(hitNumber)) {
        wins.push({
          label: "Voisins du Zéro (Trio 0/2/3)",
          betAmount: unit * 2,
          payoutRatio: "11:1",
          winAmount: unit * 2 * 12,
        });
      }
      if ([25, 26, 28, 29].includes(hitNumber)) {
        wins.push({
          label: "Voisins du Zéro (Corner 25/26/28/29)",
          betAmount: unit * 2,
          payoutRatio: "8:1",
          winAmount: unit * 2 * 9,
        });
      }
      const splits = [
        [4, 7],
        [12, 15],
        [18, 21],
        [19, 22],
        [32, 35],
      ];
      for (const [n1, n2] of splits) {
        if (hitNumber === n1 || hitNumber === n2) {
          wins.push({
            label: `Voisins du Zéro (Split ${n1}/${n2})`,
            betAmount: unit,
            payoutRatio: "17:1",
            winAmount: unit * 18,
          });
        }
      }
      continue;
    }

    // 4. French Series: Orphelins à Cheval (160)
    if (typeId === 160) {
      const unit = amt / 5;
      if (hitNumber === 1) {
        wins.push({
          label: "Orphelins (Straight 1)",
          betAmount: unit,
          payoutRatio: "35:1",
          winAmount: unit * 36,
        });
      }
      const splits = [
        [6, 9],
        [14, 17],
        [17, 20],
        [31, 34],
      ];
      for (const [n1, n2] of splits) {
        if (hitNumber === n1 || hitNumber === n2) {
          wins.push({
            label: `Orphelins (Split ${n1}/${n2})`,
            betAmount: unit,
            payoutRatio: "17:1",
            winAmount: unit * 18,
          });
        }
      }
      continue;
    }

    // 5. French Series: Tiers du Cylindre (161)
    if (typeId === 161) {
      const unit = amt / 6;
      const splits = [
        [5, 8],
        [10, 11],
        [13, 16],
        [23, 24],
        [27, 30],
        [33, 36],
      ];
      for (const [n1, n2] of splits) {
        if (hitNumber === n1 || hitNumber === n2) {
          wins.push({
            label: `Tiers (Split ${n1}/${n2})`,
            betAmount: unit,
            payoutRatio: "17:1",
            winAmount: unit * 18,
          });
        }
      }
      continue;
    }

    // 6. Finale en Plein (199 to 208)
    if (typeId >= 199 && typeId <= 208) {
      const nums = info?.numbers || [];
      if (nums.includes(hitNumber)) {
        const unit = amt / (nums.length || 1);
        wins.push({
          label: `${info.label} (Straight ${hitNumber})`,
          betAmount: unit,
          payoutRatio: "35:1",
          winAmount: unit * 36,
        });
      }
      continue;
    }

    // 7. Finale a Cheval (209 to 218)
    if (typeId >= 209 && typeId <= 218) {
      const nums = info?.numbers || [];
      const pairs = [];
      for (let i = 0; i < nums.length; i += 2) {
        if (i + 1 < nums.length) pairs.push([nums[i], nums[i + 1]]);
      }
      const unit = amt / (pairs.length || 1);
      for (const [n1, n2] of pairs) {
        if (hitNumber === n1 || hitNumber === n2) {
          wins.push({
            label: `${info.label} (Split ${n1}/${n2})`,
            betAmount: unit,
            payoutRatio: "17:1",
            winAmount: unit * 18,
          });
        }
      }
      continue;
    }

    // 8. Standard single bets with numbers and positive payout
    if (info?.numbers && info.numbers.includes(hitNumber) && info.payout > 0) {
      wins.push({
        label: info.label,
        betAmount: amt,
        payoutRatio: `${info.payout}:1`,
        winAmount: amt * (info.payout + 1),
      });
      continue;
    }

    // 9. Full Complete or other bets covering the number
    if (info?.numbers && info.numbers.includes(hitNumber)) {
      wins.push({
        label: `${info.label}`,
        betAmount: amt,
        payoutRatio: info.payout > 0 ? `${info.payout}:1` : "-",
        winAmount: info.payout > 0 ? amt * (info.payout + 1) : amt,
      });
    }
  }

  return wins;
});

const finalWinningCards = computed(() => {
  if (winningBetsBreakdown.value.length > 0) {
    return winningBetsBreakdown.value;
  }
  if (hasWinnings.value) {
    const totalWin =
      parseFloat(props.winConverted) || parseFloat(props.win) || 0;
    return [
      {
        label: "Winning Bet",
        betAmount: totalAllBetsAmount.value,
        payoutRatio: "-",
        winAmount: totalWin,
      },
    ];
  }
  return [];
});

const displayTotalWin = computed(() => {
  const winVal = parseFloat(props.winConverted) || parseFloat(props.win) || 0;
  if (winVal > 0) return formatCurrency(winVal);
  const calcSum = winningBetsBreakdown.value.reduce(
    (s, b) => s + (b.winAmount || 0),
    0,
  );
  return formatCurrency(calcSum);
});

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
const BLACK_NUMBERS = new Set([
  2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35,
]);

const row1Numbers = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36];
const row2Numbers = [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35];
const row3Numbers = [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34];

const currentSlideIndex = ref("all");

const isAllMode = computed(() => currentSlideIndex.value === "all");

// DOM Refs for auto-scrolling
const pillsContainer = ref(null);
const pillAllBtn = ref(null);
const pillRefs = ref({});
const tableContainer = ref(null);

function scrollToActivePill() {
  nextTick(() => {
    const container = pillsContainer.value;
    if (!container) return;

    let targetEl = null;
    if (isAllMode.value) {
      targetEl = pillAllBtn.value;
    } else if (pillRefs.value[currentSlideIndex.value]) {
      targetEl = pillRefs.value[currentSlideIndex.value];
    }

    if (targetEl) {
      const scrollLeft =
        targetEl.offsetLeft -
        container.offsetWidth / 2 +
        targetEl.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: "smooth",
      });
    }
  });
}

function scrollToActiveTableElement() {
  nextTick(() => {
    const container = tableContainer.value;
    if (!container || container.scrollWidth <= container.clientWidth) return;

    if (isAllMode.value) {
      container.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    const targetEl = container.querySelector(
      ".cell-highlight-number, .cell-highlight-outside, .board-chip"
    );
    if (targetEl) {
      const scrollLeft =
        targetEl.offsetLeft -
        container.offsetWidth / 2 +
        targetEl.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: "smooth",
      });
    }
  });
}

function scrollToActive() {
  scrollToActivePill();
  scrollToActiveTableElement();
}

// Watch active slide changes to smoothly scroll visual items into view
watch(
  () => currentSlideIndex.value,
  () => {
    scrollToActive();
  }
);

// Watch view tab switch
watch(
  () => activeViewTab.value,
  () => {
    scrollToActiveTableElement();
  }
);

// Watch bets to reset current slide index when bets change
watch(
  () => props.bets,
  () => {
    currentSlideIndex.value = "all";
    pillRefs.value = {};
    scrollToActive();
  },
  { deep: true },
);

function isRed(num) {
  return RED_NUMBERS.has(num);
}

function getChipColorClass(val) {
  if (val === undefined || val === null || val === "")
    return "chip-color-neutral";
  const num = Number(val);
  if (num === 0) return "chip-color-green";
  if (RED_NUMBERS.has(num)) return "chip-color-red";
  if (BLACK_NUMBERS.has(num)) return "chip-color-black";
  return "chip-color-neutral";
}

function isWinningNumber(num) {
  if (
    props.winningNumber === null ||
    props.winningNumber === undefined ||
    props.winningNumber === ""
  ) {
    return false;
  }
  return Number(props.winningNumber) === Number(num);
}

function formatAmount(val) {
  if (val === undefined || val === null) return "0.00";
  return parseFloat(val).toFixed(2);
}

// Build list of slides: only individual bets one by one
const slides = computed(() => {
  if (!props.bets || props.bets.length === 0) return [];

  return props.bets.map((bet) => {
    const typeIdStr = String(bet.type_id);
    const info = rouletteBetTypes[typeIdStr] || {
      type_id: bet.type_id,
      label: `Type #${bet.type_id}`,
      category: "Custom Bet",
      payout: 0,
      numbers: [],
    };

    return {
      bet,
      amount: bet.amount,
      info,
    };
  });
});

const currentSlide = computed(() => {
  if (slides.value.length === 0) return null;
  if (isAllMode.value) return slides.value[0];
  return slides.value[currentSlideIndex.value] || slides.value[0];
});

const totalAllBetsAmount = computed(() => {
  return slides.value.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
});

function prevSlide() {
  if (slides.value.length <= 1) return;
  if (isAllMode.value) {
    currentSlideIndex.value = slides.value.length - 1;
  } else if (currentSlideIndex.value === 0) {
    currentSlideIndex.value = "all";
  } else {
    currentSlideIndex.value = currentSlideIndex.value - 1;
  }
}

function nextSlide() {
  if (slides.value.length <= 1) return;
  if (isAllMode.value) {
    currentSlideIndex.value = 0;
  } else if (currentSlideIndex.value === slides.value.length - 1) {
    currentSlideIndex.value = "all";
  } else {
    currentSlideIndex.value = currentSlideIndex.value + 1;
  }
}

// Set of covered numbers
const currentCoveredNumbers = computed(() => {
  const set = new Set();
  if (slides.value.length === 0) return set;

  if (isAllMode.value) {
    for (const s of slides.value) {
      if (s.info && s.info.numbers) {
        s.info.numbers.forEach((n) => set.add(n));
      }
    }
  } else if (currentSlide.value?.info?.numbers) {
    currentSlide.value.info.numbers.forEach((n) => set.add(n));
  }

  return set;
});

const sortedCoveredNumbers = computed(() => {
  return Array.from(currentCoveredNumbers.value).sort((a, b) => a - b);
});

function isNumberCovered(num) {
  return currentCoveredNumbers.value.has(num);
}

function isTypeIdActive(typeId) {
  if (slides.value.length === 0) return false;
  if (isAllMode.value) {
    return slides.value.some((s) => Number(s.bet?.type_id) === Number(typeId));
  }
  return Number(currentSlide.value?.bet?.type_id) === Number(typeId);
}

function formatChipLabel(amt) {
  if (amt == null || amt <= 0) return "";
  return parseFloat(amt).toFixed(2);
}

function getChipOnTypeId(typeId) {
  if (slides.value.length === 0) return null;

  let sum = 0;
  if (isAllMode.value) {
    for (const s of slides.value) {
      if (Number(s.bet?.type_id) === Number(typeId)) {
        sum += parseFloat(s.bet?.amount) || 0;
      }
    }
  } else if (Number(currentSlide.value?.bet?.type_id) === Number(typeId)) {
    sum = parseFloat(currentSlide.value?.bet?.amount) || 0;
  }

  return sum > 0 ? formatChipLabel(sum) : null;
}

function getZeroChipForBet(bet) {
  const typeId = Number(bet?.type_id);
  const totalAmount = parseFloat(bet?.amount) || 0;
  if (totalAmount <= 0) return 0;

  // Straight Up 0 (type_id 1)
  if (typeId === 1) return totalAmount;

  // Full Complete 0 (17 total units: 1 Straight on 0)
  if (typeId === 162) return totalAmount / 17;

  // Finale en Plein 0 (4 straight bets: 0, 10, 20, 30)
  if (typeId === 199) return totalAmount / 4;

  return 0;
}

const chipOnZero = computed(() => {
  if (slides.value.length === 0) return null;

  let sum = 0;
  if (isAllMode.value) {
    for (const slide of slides.value) {
      sum += getZeroChipForBet(slide.bet);
    }
  } else if (currentSlide.value) {
    sum = getZeroChipForBet(currentSlide.value.bet);
  }

  return sum > 0 ? formatChipLabel(sum) : null;
});

function getInsideChipsForBet(bet) {
  const typeId = Number(bet?.type_id);
  const totalAmount = parseFloat(bet?.amount) || 0;
  if (totalAmount <= 0) return [];

  const typeIdStr = String(typeId);
  const info = rouletteBetTypes[typeIdStr];

  // Coordinate helper for numbers 1 to 36
  const getPos = (num) => {
    const col = Math.floor((num - 1) / 3);
    const row = 2 - ((num - 1) % 3);
    return {
      x: ((col + 0.5) / 12) * 100,
      y: ((row + 0.5) / 3) * 100,
      col,
      row,
    };
  };

  const getSplitPos = (n1, n2) => {
    if (n1 === 0 || n2 === 0) {
      const other = n1 === 0 ? n2 : n1;
      const p = getPos(other);
      return { x: 0, y: p.y };
    }
    const p1 = getPos(n1);
    const p2 = getPos(n2);
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  };

  const getCornerPos = (n1, n2, n3, n4) => {
    const nums = [n1, n2, n3, n4];
    const minCol = Math.min(...nums.map((n) => Math.floor((n - 1) / 3)));
    const minRow = Math.min(...nums.map((n) => 2 - ((n - 1) % 3)));
    return {
      x: ((minCol + 1) / 12) * 100,
      y: ((minRow + 1) / 3) * 100,
    };
  };

  const getTrioPos = (nums) => {
    if (nums.includes(0)) {
      if (nums.includes(3)) return { x: 0, y: (1 / 3) * 100 };
      if (nums.includes(1)) return { x: 0, y: (2 / 3) * 100 };
    }
    const col = Math.floor((nums[0] - 1) / 3);
    return { x: ((col + 0.5) / 12) * 100, y: 100 };
  };

  // 1. Straight Up (1 to 37 -> numbers 0 to 36)
  if (typeId >= 1 && typeId <= 37) {
    const num = typeId - 1;
    if (num >= 1 && num <= 36) {
      const pos = getPos(num);
      return [{ x: pos.x, y: pos.y, amount: totalAmount }];
    }
    return [];
  }

  // 2. Full Complete (162 to 198 -> central number is typeId - 162)
  if (typeId >= 162 && typeId <= 198) {
    const n = typeId - 162;
    const subBets = [];

    if (n === 0) {
      subBets.push({ x: 0, y: (0.5 / 3) * 100, weight: 2 });
      subBets.push({ x: 0, y: (1 / 3) * 100, weight: 3 });
      subBets.push({ x: 0, y: (1.5 / 3) * 100, weight: 2 });
      subBets.push({ x: 0, y: (2 / 3) * 100, weight: 3 });
      subBets.push({ x: 0, y: (2.5 / 3) * 100, weight: 2 });
      subBets.push({ x: 0, y: 100, weight: 4 });

      const unitVal = totalAmount / 17;
      return subBets.map((b) => ({
        x: b.x,
        y: b.y,
        amount: b.weight * unitVal,
      }));
    } else {
      const c = Math.floor((n - 1) / 3);
      const r = 2 - ((n - 1) % 3);

      subBets.push({
        x: ((c + 0.5) / 12) * 100,
        y: ((r + 0.5) / 3) * 100,
        weight: 1,
      });

      if (c > 0)
        subBets.push({
          x: (c / 12) * 100,
          y: ((r + 0.5) / 3) * 100,
          weight: 2,
        });
      else subBets.push({ x: 0, y: ((r + 0.5) / 3) * 100, weight: 2 });

      if (c < 11)
        subBets.push({
          x: ((c + 1) / 12) * 100,
          y: ((r + 0.5) / 3) * 100,
          weight: 2,
        });
      if (r > 0)
        subBets.push({
          x: ((c + 0.5) / 12) * 100,
          y: (r / 3) * 100,
          weight: 2,
        });
      if (r < 2)
        subBets.push({
          x: ((c + 0.5) / 12) * 100,
          y: ((r + 1) / 3) * 100,
          weight: 2,
        });

      if (c > 0) {
        subBets.push({ x: ((c + 0.5) / 12) * 100, y: 100, weight: 3 });
      } else {
        subBets.push({ x: (0.5 / 12) * 100, y: 100, weight: 3 });
        if (n === 1 || n === 2)
          subBets.push({ x: 0, y: (2 / 3) * 100, weight: 3 });
        if (n === 2 || n === 3)
          subBets.push({ x: 0, y: (1 / 3) * 100, weight: 3 });
      }

      if (c > 0) {
        if (r > 0)
          subBets.push({ x: (c / 12) * 100, y: (r / 3) * 100, weight: 4 });
        if (r > 0 && c < 11)
          subBets.push({
            x: ((c + 1) / 12) * 100,
            y: (r / 3) * 100,
            weight: 4,
          });
        if (r < 2)
          subBets.push({
            x: (c / 12) * 100,
            y: ((r + 1) / 3) * 100,
            weight: 4,
          });
        if (r < 2 && c < 11)
          subBets.push({
            x: ((c + 1) / 12) * 100,
            y: ((r + 1) / 3) * 100,
            weight: 4,
          });
      } else {
        subBets.push({ x: 0, y: 100, weight: 4 });
        if (r > 0)
          subBets.push({ x: (1 / 12) * 100, y: (r / 3) * 100, weight: 4 });
        if (r < 2)
          subBets.push({
            x: (1 / 12) * 100,
            y: ((r + 1) / 3) * 100,
            weight: 4,
          });
      }

      if (c > 0) subBets.push({ x: (c / 12) * 100, y: 100, weight: 6 });
      if (c < 11) subBets.push({ x: ((c + 1) / 12) * 100, y: 100, weight: 6 });

      const totalWeights = subBets.reduce((sum, b) => sum + b.weight, 0);
      const unitVal = totalAmount / totalWeights;

      return subBets.map((b) => ({
        x: b.x,
        y: b.y,
        amount: b.weight * unitVal,
      }));
    }
  }

  // 3. Splits (38 to 97)
  if (typeId >= 38 && typeId <= 97) {
    const nums = info?.numbers || [];
    if (nums.length === 2) {
      const [n1, n2] = nums;
      if (n1 === 0 || n2 === 0) {
        const other = n1 === 0 ? n2 : n1;
        const pos = getPos(other);
        return [{ x: 0, y: pos.y, amount: totalAmount }];
      }
      const p1 = getPos(n1);
      const p2 = getPos(n2);
      return [
        {
          x: (p1.x + p2.x) / 2,
          y: (p1.y + p2.y) / 2,
          amount: totalAmount,
        },
      ];
    }
  }

  // 4. Street (98 to 109)
  if (typeId >= 98 && typeId <= 109) {
    const nums = info?.numbers || [];
    if (nums.length === 3) {
      const col = Math.floor((nums[0] - 1) / 3);
      return [
        {
          x: ((col + 0.5) / 12) * 100,
          y: 100,
          amount: totalAmount,
        },
      ];
    }
  }

  // 5. Corner (110 to 133)
  if (typeId >= 110 && typeId <= 133) {
    const nums = info?.numbers || [];
    if (nums.length === 4) {
      const minCol = Math.min(...nums.map((n) => Math.floor((n - 1) / 3)));
      const minRow = Math.min(...nums.map((n) => 2 - ((n - 1) % 3)));
      return [
        {
          x: ((minCol + 1) / 12) * 100,
          y: ((minRow + 1) / 3) * 100,
          amount: totalAmount,
        },
      ];
    }
  }

  // 6. Six Line (134 to 145)
  if (typeId >= 134 && typeId <= 145) {
    const nums = info?.numbers || [];
    if (nums.length === 6) {
      const minCol = Math.min(...nums.map((n) => Math.floor((n - 1) / 3)));
      return [
        {
          x: ((minCol + 1) / 12) * 100,
          y: 100,
          amount: totalAmount,
        },
      ];
    }
  }

  // 7. Finale en Plein (199 to 208)
  if (typeId >= 199 && typeId <= 208) {
    const nums = info?.numbers || [];
    const pieceAmount = totalAmount / (nums.length || 1);
    return nums
      .filter((n) => n !== 0)
      .map((n) => {
        const pos = getPos(n);
        return { x: pos.x, y: pos.y, amount: pieceAmount };
      });
  }

  // 8. Finale a Cheval (209 to 218)
  if (typeId >= 209 && typeId <= 218) {
    const nums = info?.numbers || [];
    const pairs = [];
    for (let i = 0; i < nums.length; i += 2) {
      if (i + 1 < nums.length) pairs.push([nums[i], nums[i + 1]]);
    }
    const pieceAmount = totalAmount / (pairs.length || 1);
    return pairs.map(([n1, n2]) => {
      if (n1 === 0 || n2 === 0) {
        const other = n1 === 0 ? n2 : n1;
        const pos = getPos(other);
        return { x: 0, y: pos.y, amount: pieceAmount };
      }
      const p1 = getPos(n1);
      const p2 = getPos(n2);
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
        amount: pieceAmount,
      };
    });
  }

  // 9. Series / French Call Bets (158 to 161)
  if (typeId === 158) {
    // Jeu Zéro (4 chips): Straight 26, Split 0/3, Split 12/15, Split 32/35
    const unit = totalAmount / 4;
    return [
      { ...getPos(26), amount: unit },
      { ...getSplitPos(0, 3), amount: unit },
      { ...getSplitPos(12, 15), amount: unit },
      { ...getSplitPos(32, 35), amount: unit },
    ];
  }

  if (typeId === 159) {
    // Voisins du Zéro (9 chips total):
    // 2 chips on Trio 0/2/3, 2 chips on Corner 25/26/28/29, 1 chip each on Splits 4/7, 12/15, 18/21, 19/22, 32/35
    const unit = totalAmount / 9;
    return [
      { x: 0, y: (1 / 3) * 100, amount: unit * 2 }, // Trio 0/2/3
      { ...getCornerPos(25, 26, 28, 29), amount: unit * 2 },
      { ...getSplitPos(4, 7), amount: unit },
      { ...getSplitPos(12, 15), amount: unit },
      { ...getSplitPos(18, 21), amount: unit },
      { ...getSplitPos(19, 22), amount: unit },
      { ...getSplitPos(32, 35), amount: unit },
    ];
  }

  if (typeId === 160) {
    // Orphelins à Cheval (5 chips total):
    // 1 chip on Straight 1, 1 chip each on Splits 6/9, 14/17, 17/20, 31/34
    const unit = totalAmount / 5;
    return [
      { ...getPos(1), amount: unit },
      { ...getSplitPos(6, 9), amount: unit },
      { ...getSplitPos(14, 17), amount: unit },
      { ...getSplitPos(17, 20), amount: unit },
      { ...getSplitPos(31, 34), amount: unit },
    ];
  }

  if (typeId === 161) {
    // Tiers du Cylindre (6 chips total):
    // 1 chip each on Splits 5/8, 10/11, 13/16, 23/24, 27/30, 33/36
    const unit = totalAmount / 6;
    const splits = [
      [5, 8],
      [10, 11],
      [13, 16],
      [23, 24],
      [27, 30],
      [33, 36],
    ];
    return splits.map(([n1, n2]) => ({
      ...getSplitPos(n1, n2),
      amount: unit,
    }));
  }

  return [];
}

const activeInsideChips = computed(() => {
  if (slides.value.length === 0) return [];

  const rawChips = [];

  if (isAllMode.value) {
    for (const slide of slides.value) {
      const chips = getInsideChipsForBet(slide.bet);
      rawChips.push(...chips);
    }
  } else if (currentSlide.value) {
    rawChips.push(...getInsideChipsForBet(currentSlide.value.bet));
  }

  // Group chips by approximate location (x, y) rounded to 1 decimal place so they merge nicely
  const grouped = new Map();
  for (const c of rawChips) {
    const key = `${c.x.toFixed(1)}_${c.y.toFixed(1)}`;
    if (!grouped.has(key)) {
      grouped.set(key, { x: c.x, y: c.y, total: 0 });
    }
    grouped.get(key).total += Number(c.amount) || 0;
  }

  return Array.from(grouped.values()).map((g) => ({
    x: g.x,
    y: g.y,
    amount: formatChipLabel(g.total),
  }));
});
</script>

<style scoped>
.roulette-bet-carousel-wrapper {
  box-sizing: border-box;
}

.roulette-board-container {
  max-width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}

.roulette-board-cloth {
  min-width: 100%;
}

.roulette-top-grid {
  display: grid;
  grid-template-columns: 36px 1fr 38px;
  gap: 0px;
  width: 100%;
}

.numbers-grid {
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.grid-row {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 0px;
}

.columns-bet-group {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  gap: 0px;
}

.roulette-dozens-row,
.roulette-outside-row {
  display: grid;
  gap: 0px;
  margin-top: 0px;
  min-width: 580px;
}

.roulette-dozens-row {
  grid-template-columns: 36px repeat(3, 1fr) 38px;
}

.roulette-outside-row {
  grid-template-columns: 36px repeat(6, 1fr) 38px;
}

.cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 700;
  transition: all 0.2s ease-in-out;
  user-select: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.cell-zero {
  background: linear-gradient(135deg, #059669, #047857);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  min-height: 80px;
}

.cell-number {
  height: 25px;
  font-size: 11px;
  font-weight: 800;
  aspect-ratio: 1;
  min-height: 50px;
  width: 100%;
  border-radius: 0 !important;
}

.cell-red {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #ffffff;
}

.cell-black {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: #ffffff;
}

.cell-column {
  height: auto;
  background: #047857;
  color: #a7f3d0;
  font-size: 10px;
  font-weight: 700;
  flex-grow: 1;
}

.cell-dozen {
  height: 24px;
  background: #047857;
  color: #d1fae5;
  font-size: 10px;
  font-weight: 700;
}

.cell-outside {
  height: 24px;
  background: #047857;
  color: #d1fae5;
  font-size: 10px;
  font-weight: 700;
}

.cell-red-outside {
  background: #991b1b;
}

.cell-black-outside {
  background: #0f172a;
}

.diamond-red {
  width: 13px;
  height: 13px;
  background: #ef4444;
  transform: rotate(45deg);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 2px;
}

.diamond-black {
  width: 13px;
  height: 13px;
  background: #000000;
  transform: rotate(45deg);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 2px;
}

.dozen-spacer,
.dozen-spacer-right {
  visibility: hidden;
}

/* HIGHLIGHT STYLES (WHITE INNER BORDER / SHADOW TO INDICATE COVERED NUMBERS) */
.cell-highlight-number {
  color: #ffffff !important;
  font-weight: 900 !important;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.9),
    inset 0 0 5px rgba(255, 255, 255, 0.4) !important;
  z-index: 10;
}

.cell-highlight-outside {
  color: #ffffff !important;
  font-weight: 900 !important;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.9),
    inset 0 0 5px rgba(255, 255, 255, 0.4) !important;
  z-index: 10;
}

/* WINNING EXTRACTED NUMBER RING MARKER */
.winning-marker-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 28px;
  height: 28px;
  border: 2.5px solid #facc15;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(250, 204, 21, 0.85),
    inset 0 0 4px rgba(250, 204, 21, 0.4);
  pointer-events: none;
  z-index: 15;
}

/* 3D BOARD CHIP (COORDINATED WITH RACETRACK CHIP STYLE) */
.board-chip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #2563eb;
  border: 1.5px solid #ffffff;
  color: #ffffff;
  font-size: 7px;
  font-weight: 800;
  font-family: inherit;
  letter-spacing: -0.02em;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  pointer-events: none;
  z-index: 20;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}

.board-chip::after {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.75);
  pointer-events: none;
}

/* =========================================
   3D ROULETTE CHIPS
========================================= */
.roulette-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: inherit;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  user-select: none;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: transform 0.15s ease;
}

.roulette-chip:hover {
  transform: translateY(-1px) scale(1.1);
}

.chip-number {
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
}

.chip-table {
  width: 24px;
  height: 24px;
  font-size: 10px;
}

.chip-color-red {
  background: radial-gradient(
    circle at 35% 30%,
    #ff5252 0%,
    #e11d48 55%,
    #9f1239 100%
  );
  border: 1.5px solid #fecdd3;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(225, 29, 72, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

.chip-color-black {
  background: radial-gradient(
    circle at 35% 30%,
    #475569 0%,
    #1e293b 65%,
    #0f172a 100%
  );
  border: 1.5px solid #94a3b8;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.3);
}

.chip-color-green {
  background: radial-gradient(
    circle at 35% 30%,
    #34d399 0%,
    #059669 60%,
    #064e3b 100%
  );
  border: 1.5px solid #a7f3d0;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(5, 150, 105, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

.chip-color-neutral {
  background: radial-gradient(
    circle at 35% 30%,
    #818cf8 0%,
    #4f46e5 60%,
    #312e81 100%
  );
  border: 1.5px solid #c7d2fe;
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(79, 70, 229, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.45);
}

/* Scrollbar styling */
.scrollbar-thin::-webkit-scrollbar {
  height: 4px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
</style>
